import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { notificationAPI } from '@/services/api';
import { useState, useEffect, useMemo } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import { Menu, Settings, HelpCircle, LogOut, Sun, Moon, Clock, CloudSun, Bell, CheckCheck, Search } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

const pageTitles = {
  '/app/dashboard': { title: 'Dashboard', subtitle: 'Overview of your scheduling activity' },
  '/app/appointments': { title: 'Appointments', subtitle: 'Manage your bookings' },
  '/app/clients': { title: 'Clients', subtitle: 'Your client directory' },
  '/app/calendars': { title: 'Calendars', subtitle: 'Manage your booking calendars' },
  '/app/team': { title: 'Team', subtitle: 'Team members and permissions' },
  '/app/organization': { title: 'Organization', subtitle: 'Organization settings' },
  '/app/billing': { title: 'Billing', subtitle: 'Subscription and invoices' },
  '/app/profile': { title: 'Profile', subtitle: 'Your account details' },
  '/app/email-templates': { title: 'Email Templates', subtitle: 'Customize your emails' },
  '/app/personalization': { title: 'Personalization', subtitle: 'Brand and appearance' },
  '/app/settings': { title: 'Settings', subtitle: 'App configuration' },
  '/app/guide': { title: 'Guide', subtitle: 'Help and documentation' },
};

export default function Navbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const pageInfo = useMemo(() => {
    for (const [path, info] of Object.entries(pageTitles)) {
      if (location.pathname === path || location.pathname.startsWith(path + '/')) return info;
    }
    return { title: 'ScheduleX', subtitle: '' };
  }, [location.pathname]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await notificationAPI.getAll({ limit: 10 });
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      } catch {}
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const res = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code`
            );
            const data = await res.json();
            if (data.current) {
              setWeather({
                temp: Math.round(data.current.temperature_2m),
                unit: '°C',
                code: data.current.weather_code
              });
            }
          } catch {}
        },
        () => {}
      );
    }
  }, []);

  const getWeatherEmoji = (code) => {
    if (code <= 1) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '☁️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '🌨️';
    if (code <= 82) return '🌧️';
    if (code <= 86) return '❄️';
    if (code <= 99) return '⛈️';
    return '🌤️';
  };

  const timeStr = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header
      className="h-16 shrink-0 sticky top-0 z-[40] flex items-center px-6 lg:px-8"
      style={{
        borderBottom: '1px solid var(--dash-border)',
        background: 'color-mix(in srgb, var(--dash-bg) 80%, transparent)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      <div className="flex items-center justify-between w-full">
        {/* Left */}
        <div className="flex items-center gap-4">
          <button
            className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer"
            style={{ background: 'var(--dash-surface)', border: '1px solid var(--dash-border)' }}
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
          >
            <Menu className="w-[18px] h-[18px]" style={{ color: 'var(--dash-text2)' }} />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--dash-text)' }}>{pageInfo.title}</h1>
            <p className="text-xs" style={{ color: 'var(--dash-text3)' }}>{pageInfo.subtitle}</p>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          {/* Time */}
          <div className="hidden sm:flex items-center gap-1.5 text-sm" style={{ color: 'var(--dash-text3)' }}>
            <Clock className="w-3.5 h-3.5" />
            <span className="font-mono tabular-nums">{timeStr}</span>
          </div>

          {/* Weather */}
          {weather && (
            <div className="hidden sm:flex items-center gap-1 text-sm" style={{ color: 'var(--dash-text3)' }}>
              <span>{getWeatherEmoji(weather.code)}</span>
              <span>{weather.temp}{weather.unit}</span>
            </div>
          )}

          {/* Divider */}
          <div className="hidden sm:block w-px h-5" style={{ background: 'var(--dash-border)' }} />

          {/* Notification bell */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer relative"
                style={{ background: 'var(--dash-surface)', border: '1px solid var(--dash-border)', transition: 'background .2s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dash-surface2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--dash-surface)'}
                aria-label="Notifications"
              >
                <Bell className="w-[17px] h-[17px]" style={{ color: 'var(--dash-text3)' }} />
                {unreadCount > 0 && (
                  <div
                    className="absolute top-[6px] right-[6px] w-[7px] h-[7px] rounded-full"
                    style={{ background: 'var(--dash-accent)', animation: 'dash-pulse-dot 2s ease-in-out infinite' }}
                  />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0 border-[var(--dash-border)] bg-[var(--dash-bg2)]">
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--dash-border)' }}>
                <h4 className="text-sm font-semibold" style={{ color: 'var(--dash-text)' }}>Notifications</h4>
                {unreadCount > 0 && (
                  <button
                    className="text-xs font-medium cursor-pointer flex items-center gap-1 px-2 py-1 rounded"
                    style={{ color: 'var(--dash-text3)', background: 'none', border: 'none', fontFamily: 'inherit' }}
                    onClick={async () => {
                      try {
                        await notificationAPI.markAllRead();
                        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                        setUnreadCount(0);
                      } catch {}
                    }}
                  >
                    <CheckCheck className="w-3 h-3" /> Mark all read
                  </button>
                )}
              </div>
              <ScrollArea className="max-h-80">
                {notifications.length === 0 ? (
                  <p className="text-sm text-center py-8" style={{ color: 'var(--dash-text3)' }}>No notifications yet</p>
                ) : (
                  <div className="divide-y" style={{ borderColor: 'var(--dash-border)' }}>
                    {notifications.map((n) => (
                      <div
                        key={n._id}
                        className="px-4 py-3 cursor-pointer transition-colors"
                        style={{ borderColor: 'var(--dash-border)', background: !n.read ? 'var(--dash-nav-active)' : 'transparent' }}
                        onClick={async () => {
                          if (!n.read) {
                            try {
                              await notificationAPI.markRead(n._id);
                              setNotifications(prev => prev.map(item => item._id === n._id ? { ...item, read: true } : item));
                              setUnreadCount(prev => Math.max(0, prev - 1));
                            } catch {}
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: 'var(--dash-accent)' }} />}
                          <div className={!n.read ? '' : 'ml-4'}>
                            <p className="text-sm font-medium" style={{ color: 'var(--dash-text)' }}>{n.title}</p>
                            <p className="text-xs line-clamp-2" style={{ color: 'var(--dash-text3)' }}>{n.message}</p>
                            <p className="text-[10px] mt-1" style={{ color: 'var(--dash-text4)' }}>
                              {new Date(n.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>

          {/* Search */}
          <button
            className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer"
            style={{ background: 'var(--dash-surface)', border: '1px solid var(--dash-border)', transition: 'background .2s' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dash-surface2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--dash-surface)'}
            onClick={() => {}}
            aria-label="Search"
          >
            <Search className="w-[17px] h-[17px]" style={{ color: 'var(--dash-text3)' }} />
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="w-9 h-5 rounded-full relative cursor-pointer transition-all"
            style={{ background: 'var(--dash-toggle-bg)', border: '1px solid var(--dash-border)', flexShrink: 0 }}
            aria-label="Toggle theme"
          >
            <div
              className="absolute top-[1.5px] left-[1.5px] w-[15px] h-[15px] rounded-full flex items-center justify-center transition-transform"
              style={{
                background: 'var(--dash-toggle-dot)',
                transform: theme === 'light' ? 'translateX(16px)' : 'translateX(0)',
                transition: 'transform .3s cubic-bezier(.16,1,.3,1)',
              }}
            >
              {theme === 'dark' ? (
                <Sun className="w-2.5 h-2.5" style={{ color: 'var(--dash-bg)' }} />
              ) : (
                <Moon className="w-2.5 h-2.5" style={{ color: 'var(--dash-bg)' }} />
              )}
            </div>
          </button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full p-0 cursor-pointer overflow-hidden"
                style={{ background: 'var(--dash-surface2)' }}
                aria-label="User menu"
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ background: 'var(--dash-surface3)', color: 'var(--dash-text)' }}
                  >
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 border-[var(--dash-border)] bg-[var(--dash-bg2)]">
              <div className="px-2 py-2">
                <p className="text-sm font-medium" style={{ color: 'var(--dash-text)' }}>{user?.name}</p>
                <p className="text-xs truncate" style={{ color: 'var(--dash-text3)' }}>{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/app/settings')} className="cursor-pointer" style={{ color: 'var(--dash-text2)' }}>
                <Settings className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/guide')} className="cursor-pointer" style={{ color: 'var(--dash-text2)' }}>
                <HelpCircle className="w-4 h-4 mr-2" /> Guide
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
